/**
 * Lookups — DESIGN.md §6.6, §6.7.
 *
 * Statuses, tags, and custom fields back every picker in the app, change
 * rarely, and are small. One query, cached hard, invalidated by the
 * `lookup.changed` SSE envelope (§15).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Lookups, Status, UserRef } from '@slate/shared';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/keys';

export function useLookups(): UseQueryResult<Lookups, Error> {
  return useQuery({
    queryKey: queryKeys.lookups,
    queryFn: () => http.get<Lookups>('/lookups'),
    staleTime: 5 * 60_000,
  });
}

/** Every member, for assignee pickers and @mention typeahead (§6.9). */
export function useUserRefs(): UseQueryResult<UserRef[], Error> {
  return useQuery({
    queryKey: queryKeys.userRefs,
    queryFn: () => http.get<UserRef[]>('/users/refs'),
    staleTime: 5 * 60_000,
  });
}

/** Board order, archived statuses excluded — what a status dropdown shows. */
export function activeStatuses(lookups: Lookups | undefined): Status[] {
  return (lookups?.statuses ?? []).filter((status) => !status.archivedAt);
}

export function statusById(lookups: Lookups | undefined, id: string): Status | undefined {
  return lookups?.statuses.find((status) => status.id === id);
}
